import { NavLink, useParams } from "react-router-dom";
import {
  Box,
  Heading,
  Text,
  Flex,
  Grid,
  Tag,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  TagLabel,
  TableContainer,
  IconButton,
  Tooltip,
  Link,
  HStack,
} from "@chakra-ui/react";
import { useAppContext } from "../../../contexts/AppContext";
import { ChevronLeft, Earth, Mail } from "lucide-react";
import useCustomTheme from "../../../hooks/useCustomTheme";
import InvestorDocumentsTable from "./InvestorDocumentsTable";

const InvestorDetail = () => {
  const { fundName, investorName } = useParams();
  const { data, formatMillions, tableSize, formatDate } = useAppContext();
  const { theme } = useCustomTheme();

  // Format the fund name to match the URL and find the fund
  const formattedFundName = fundName.replace(/-/g, " ");
  const fund = data.find((fund) => fund.fundName === formattedFundName);

  if (!fund) {
    return (
      <Box p={6} textAlign="center">
        <Heading size="lg">Fund Not Found</Heading>
        <Text>The requested fund does not exist in our database.</Text>
      </Box>
    );
  }

  // Find the specific investment
  const investor = fund.investorsTable.find(
    (inv) => inv.investorName.replace(/ /g, "-") === investorName
  );

  if (!investor) {
    return (
      <Box p={6} textAlign="center">
        <Heading size="lg">Investor Not Found</Heading>
        <Text>The requested investor does not exist in our database.</Text>
      </Box>
    );
  }




  return (
    <Flex direction="column" gap="8">
    
    <Flex justify={{ lg: "space-between", base: "flex-start" }} w={"full"} align={"center"} direction={{ lg: "row", base: "column" }} gap={4}>
            
            <Flex align={"center"} gap={4}>
              <IconButton
                aria-label="Back"
                variant={"ghost"}
                icon={<ChevronLeft  />}
                as={NavLink}
                to={`/funds/${fund.fundName}#investors`}
                size={{ lg: "md", base: "xs" }}
              />
              <h1>{investor.investorName}</h1>
              <Tag gap={2} size={"lg"}>
                <Box
                  as="span"
                  bg={fund.investmentStrategy.color}
                  h="8px"
                  w="8px"
                  borderRadius={"full"}
                />
                <TagLabel>{investor.type}</TagLabel>
              </Tag>
            </Flex>
        
        
        <HStack direction={"row"} gap={8}>
          <Tooltip label="Tax Residence">
            <Flex
              gap={2}
              direction={"row"}
              alignItems={"center"}
              display={"flex"}
              fontWeight={500}>
              <Earth size={16} color={theme.colors.grey[500]} />
              {investor.taxResidence}
            </Flex>
          </Tooltip>

          <Tooltip label={investor.contactDetails}>
            <Flex
              as={Link}
              href={`mailto:${investor.contactDetails}`}
              gap={2}
              direction={"row"}
              alignItems={"center"}
              display={"flex"}
              fontWeight={500}>
              <Mail size={16} color={theme.colors.grey[500]} />
            </Flex>
          </Tooltip>

        



        </HStack>
    </Flex>




    <Tabs variant="line" colorScheme="brand">
        <TabList mb={8}>
          <Tab>Overview</Tab>
          <Tab>Documents</Tab>
        </TabList>

        <TabPanels>
        
        <TabPanel  p={0}>

    <Grid gap={8} direction="column" templateColumns={"repeat(12, 1fr)"}>
    
    <Flex gridColumn={{ lg: "span 8", base: "span 12" }} direction="column" gap={16}>      



    <Flex direction={"column"} gap={16}>


<SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}
        sx={{ 
            ".chakra-stat": { 
                border: "1px solid", 
                borderColor: "grey.200",
                p: 4,
                borderRadius: 8
            }
            }}   >
     <Stat>
            <StatLabel>Total Commitment</StatLabel>
            <StatNumber>{fund.fundCurrency.ISO} {formatMillions(investor.investmentDetails.totalCommitment)}</StatNumber>
          </Stat>
        <Stat>
          <StatLabel>Net IRR</StatLabel>
          <StatNumber>{investor.performanceMetrics.netIRR}%</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>Gross IRR</StatLabel>
          <StatNumber>{investor.performanceMetrics.grossIRR}%</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>DPI</StatLabel>
          <StatNumber>{investor.performanceMetrics.dpi}x</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>RVPI</StatLabel>
          <StatNumber>{investor.performanceMetrics.rvpi}x</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>TVPI</StatLabel>
          <StatNumber>{investor.performanceMetrics.tvpi}x</StatNumber>
        </Stat>


      </SimpleGrid>



      <Flex gap={4} direction={'column'}>
      <h5>
        Capital Calls
      </h5>
      <TableContainer><Table variant="simple" size={tableSize}>
        <Thead>
          <Tr>
            <Th>Call Date</Th>
            <Th>Call Amount</Th>
            <Th>Due Date</Th>
            <Th>Payment Date</Th>
            <Th>Payment Status</Th>
          </Tr>
        </Thead>
        <Tbody>
          {investor.capitalCalls.map((call, index) => (
            <Tr key={index}>
              <Td><Badge>{formatDate(call.callDate)}</Badge></Td>
              <Td>{formatMillions(call.callAmount)}</Td>

              
              <Td><Badge>{formatDate(call.dueDate)}</Badge></Td>
              <Td><Badge>{formatDate(call.paymentDate || "N/A")}</Badge></Td>
              <Td>
                <Badge
                  colorScheme={call.paymentStatus === "Paid" ? "green" : "red"}
                >
                  {call.paymentStatus}
                </Badge>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table></TableContainer></Flex>

      <Flex gap={4} direction={'column'}>

      <h5>
        Distributions
      </h5>
      <TableContainer>
        <Table variant="simple" size={tableSize}>
        <Thead>
          <Tr>
            <Th>Distribution Amount</Th>
            <Th>Type</Th>
            <Th>Date</Th>
            <Th>Payment Status</Th>
          </Tr>
        </Thead>
        <Tbody>
          {investor.distributions.map((distribution, index) => (
            <Tr key={index}>
              <Td>{formatMillions(distribution.amount)}</Td>
              <Td>{distribution.type}</Td>
              <Td><Badge>{formatDate(distribution.date)}</Badge></Td>
              <Td>
                <Badge
                  colorScheme={
                    distribution.paymentStatus === "Paid" ? "green" : "red"
                  }
                >
                  {distribution.paymentStatus}
                </Badge>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      </TableContainer>
      </Flex>



     

        
      </Flex>


      
      


      </Flex>


      <Flex gridColumn={{ lg: "span 4", base: "span 12" }} direction={"column"} gap={16} borderLeft={{ lg: "1px solid", base: "none" }} borderColor={{ lg: "grey.200", base: "none" }} pl={{ lg: 8, base: 0 }}>


      <SimpleGrid columns={{ base: 1, md: 1 }} spacing={4}
        >
          <Stat>
            <StatLabel>PEP Status</StatLabel>
            <StatNumber color={investor.pepStatus ? "red" : "green"}> 
            {investor.pepStatus ? "PEP" : "Non-PEP"}
         </StatNumber>
          </Stat>

          <Stat>
            <StatLabel>Risk Rating</StatLabel>
            <StatNumber> 
            {investor.riskRating}
         </StatNumber>
          </Stat>


          <Stat>
            <StatLabel>Share Class</StatLabel>
            <StatNumber>{investor.investmentDetails.shareClass}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Entry Date</StatLabel>
            <StatNumber>
            {formatDate(investor.investmentDetails.entryDate)}
            </StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Lock-up Period</StatLabel>
            <StatNumber>
            {investor.investmentDetails.lockupPeriod}
            </StatNumber>
          </Stat>
          <Stat>
              <StatLabel>Special Conditions</StatLabel>
              <StatNumber>
              {investor.investmentDetails.specialConditions}
              </StatNumber>
            </Stat>
          
           
        </SimpleGrid>

       {/* Capital Movements */}
      

       


    
    </Flex>
      </Grid>

            </TabPanel>



            <TabPanel  p={0}>
           
           <InvestorDocumentsTable investor={investor} />
      
    

            </TabPanel>


        </TabPanels>

      </Tabs>

      

     

     

     

    </Flex>
  );
};

export default InvestorDetail;
